
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { NavBar } from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import ComplianceAIForm from "@/components/compliance-ai/ComplianceAIForm";
import ComplianceAIResponse from "@/components/compliance-ai/ComplianceAIResponse";
import { ComplianceFormData } from "@/types/compliance";
import { API_CONFIG } from "@/constants/env.ts";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, LucideActivity, Shield, FileCheck, BarChart3, Wrench } from "lucide-react";
import { mockComplianceAiResponse } from "@/mocks/complianceAiResponse";
import { Button } from "@/components/ui/button";

// Loading animation component with countdown
const LoadingAnimation = ({ timeRemaining, setTimeRemaining }: { timeRemaining: number, setTimeRemaining: React.Dispatch<React.SetStateAction<number>> }) => {
  // Start the countdown timer
  useEffect(() => {
    if (timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, setTimeRemaining]);

  // Calculate progress percentage
  const progressPercentage = ((60 - timeRemaining) / 60) * 100;

  return (
    <div className="min-h-[500px] flex flex-col items-center justify-center">
      <div className="mb-8 flex flex-col items-center">
        <div className="relative w-40 h-40 mb-8">
          {/* Circular progress background */}
          <div className="absolute inset-0 rounded-full border-8 border-gray-200 dark:border-gray-800"></div>

          {/* Circular progress fill */}
          <svg className="absolute inset-0 w-full h-full rotate-[-90deg]" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="46"
              fill="none"
              stroke="currentColor"
              strokeWidth="8"
              strokeDasharray="289.27"
              strokeDashoffset={289.27 - (289.27 * progressPercentage / 100)}
              className="text-purple-500 dark:text-purple-400"
            />
          </svg>

          {/* Inner pulsing circle */}
          <div className="absolute inset-4 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
              {timeRemaining}
            </div>
          </div>

          {/* Pulsing ripples */}
          <div className="absolute inset-0 animate-ping opacity-20 rounded-full bg-purple-500 dark:bg-purple-400" style={{ animationDuration: '3s' }}></div>
          <div className="absolute inset-0 animate-ping opacity-20 rounded-full bg-purple-500 dark:bg-purple-400" style={{ animationDuration: '3s', animationDelay: '1s' }}></div>
        </div>

        <h2 className="text-2xl font-medium text-gray-900 dark:text-gray-100 mb-2">
          Analyzing Your Compliance Process
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-center max-w-md">
          Our AI consultant is evaluating your data and preparing a comprehensive analysis and optimization plan
        </p>
      </div>

      {/* Animation for the processing stages */}
      <div className="w-full max-w-md">
        <div className="space-y-6">
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${progressPercentage > 0 ? 'bg-purple-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
              {progressPercentage > 0 ? '✓' : '1'}
            </div>
            <div className="ml-4">
              <div className="font-medium">Analyzing Current Process</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Evaluating efficiency and challenges</div>
            </div>
          </div>

          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${progressPercentage > 33 ? 'bg-purple-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
              {progressPercentage > 33 ? '✓' : '2'}
            </div>
            <div className="ml-4">
              <div className="font-medium">Generating AI Recommendations</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Creating tailored optimization plan</div>
            </div>
          </div>

          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${progressPercentage > 66 ? 'bg-purple-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
              {progressPercentage > 66 ? '✓' : '3'}
            </div>
            <div className="ml-4">
              <div className="font-medium">Preparing Process Visualization</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Building visual comparison</div>
            </div>
          </div>

          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${progressPercentage >= 100 ? 'bg-purple-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
              {progressPercentage >= 100 ? '✓' : '4'}
            </div>
            <div className="ml-4">
              <div className="font-medium">Finalizing Results</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Preparing your complete report</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Mock data for development environment
const mockCompanyProfiles: ComplianceFormData[] = [
  {
    teamSize: "6-15",
    industry: "finance",
    complianceStandards: ["gdpr", "pci-dss", "sox"],
    currentTools: ["spreadsheets", "email", "docs"],
    currentChallenges: ["time", "audit", "reporting", "updates"],
    processDuration: "weeks",
    complianceProcesses: "Our team maintains documentation across multiple shared drives. Version control is managed through naming conventions. We need to improve our processes for regulatory compliance tracking and reporting.",
    softwareProcesses: "We currently use a mix of spreadsheets, email and shared drives. Our approval workflows are manual and time-consuming. We'd like to automate compliance tracking and reporting.",
    additionalInfo: "We're particularly concerned about maintaining audit trails and ensuring we can quickly respond to regulator requests."
  },
  {
    teamSize: "1-5",
    industry: "healthcare",
    complianceStandards: ["hipaa", "gdpr"],
    currentTools: ["manual", "spreadsheets", "email"],
    currentChallenges: ["time", "documentation", "updates", "visibility"],
    processDuration: "months",
    complianceProcesses: "Paper-based documentation system with manual scanning of important records. We perform quarterly compliance audits with the medical director using checklists, but follow-up is often delayed.",
    softwareProcesses: "We maintain a basic digital filing system but struggle with organization. We need a better system for tracking access to sensitive patient information.",
    additionalInfo: "Patient data security is our top priority. We need better systems for tracking access to sensitive information."
  },
  {
    teamSize: "16-30",
    industry: "technology",
    complianceStandards: ["iso27001", "gdpr", "nist", "ccpa"],
    currentTools: ["governance", "project", "chat"],
    currentChallenges: ["coordination", "reporting", "inconsistency"],
    processDuration: "continuous",
    complianceProcesses: "We use a GRC platform but still struggle with keeping documentation consistent across teams. Many departments maintain their own processes. We have a peer review process with designated compliance champions in each department.",
    softwareProcesses: "We use GRC software where compliance activities are tracked. Final approval by CISO is required. We need to better integrate our tools across global operations to standardize processes.",
    additionalInfo: "We operate globally and need to ensure we meet compliance requirements in multiple jurisdictions. Our main challenge is harmonizing processes."
  },
  {
    teamSize: "31-50",
    industry: "government",
    complianceStandards: ["fedramp", "nist"],
    currentTools: ["custom", "docs", "project"],
    currentChallenges: ["inconsistency", "audit", "time", "coordination"],
    processDuration: "months",
    complianceProcesses: "Extensive documentation requirements with multiple levels based on sensitivity and classification. We have a multi-stage review process with separate teams for technical, security, and policy compliance.",
    softwareProcesses: "We use a custom internal system for tracking documentation. Our approval system requires sign-off at different levels before proceeding, which is time-consuming. Interagency coordination is challenging.",
    additionalInfo: "We face unique challenges with classified information and interagency coordination. Documentation must meet specific formatting and retention requirements."
  },
  {
    teamSize: "6-15",
    industry: "retail",
    complianceStandards: ["pci-dss", "gdpr", "ccpa"],
    currentTools: ["spreadsheets", "email", "project"],
    currentChallenges: ["time", "updates", "visibility", "reporting"],
    processDuration: "weeks",
    complianceProcesses: "Mixture of physical and digital documentation. We operate a monthly reporting cycle with regional managers reviewing compliance in their territories, with final sign-off by the compliance director.",
    softwareProcesses: "Store-level compliance is tracked on spreadsheets that are periodically consolidated by HQ. Reports are shared via email and project management tools. We lack a centralized system for real-time visibility.",
    additionalInfo: "With multiple store locations, maintaining consistent application of policies is challenging. We need better transparency across the organization."
  },
  {
    teamSize: "50+",
    industry: "finance",
    complianceStandards: ["gdpr", "sox", "pci-dss", "iso27001"],
    currentTools: ["governance", "project", "custom"],
    currentChallenges: ["coordination", "reporting", "documentation"],
    processDuration: "continuous",
    complianceProcesses: "Enterprise GRC platform with integrated documentation, but still facing challenges with keeping policies updated across business units. We have a dedicated compliance team with specialized focus areas and monthly review cycles with executive oversight.",
    softwareProcesses: "We use automated workflows for approvals, but integration between different business units' systems remains challenging. Our cross-border data transfer processes need improvement to meet varying jurisdictional requirements.",
    additionalInfo: "As a multinational bank, we deal with complex regulatory requirements that vary by jurisdiction. Cross-border data transfers are particularly challenging."
  },
  {
    teamSize: "16-30",
    industry: "manufacturing",
    complianceStandards: ["iso27001", "gdpr"],
    currentTools: ["spreadsheets", "manual", "docs"],
    currentChallenges: ["documentation", "inconsistency", "visibility"],
    processDuration: "months",
    complianceProcesses: "Paper-based systems at production facilities with limited digital integration. We conduct quarterly compliance reviews with production managers and annual audits by external consultants.",
    softwareProcesses: "Corporate office maintains digital records but synchronization with production facilities is manual. Findings from audits are tracked in action registries but follow-up processes are inconsistent.",
    additionalInfo: "Our supply chain compliance is particularly challenging. We struggle to verify that our suppliers meet our compliance standards consistently."
  },
  {
    teamSize: "1-5",
    industry: "education",
    complianceStandards: ["gdpr", "hipaa"],
    currentTools: ["spreadsheets", "email", "manual"],
    currentChallenges: ["time", "updates", "audit", "documentation"],
    processDuration: "weeks",
    complianceProcesses: "Basic compliance process with ad-hoc reviews by administrative staff and an annual compliance check by the principal/dean. Limited formalized processes for managing student data privacy requirements.",
    softwareProcesses: "We use a basic digital filing system with shared folders. Compliance tasks are assigned via email with limited tracking capabilities. We need better tools to monitor and document compliance activities.",
    additionalInfo: "Student privacy is our primary concern. We have limited resources and struggle to keep up with changing regulations while managing day-to-day operations."
  },
  {
    teamSize: "6-15",
    industry: "energy",
    complianceStandards: ["nist", "iso27001"],
    currentTools: ["project", "docs", "email"],
    currentChallenges: ["coordination", "audit", "reporting", "time"],
    processDuration: "months",
    complianceProcesses: "Monthly safety and compliance meetings with department representatives and quarterly executive review of critical compliance areas. Our environmental impact and safety protocols require extensive documentation and auditing.",
    softwareProcesses: "We use a document management system with version control, but it's isolated from operational systems. Field reporting is done through mobile apps that don't integrate well with our main compliance systems.",
    additionalInfo: "Regulatory requirements for our industry are extensive, particularly around environmental impact and safety protocols. We need better integration between safety and security compliance efforts."
  },
  {
    teamSize: "16-30",
    industry: "telecom",
    complianceStandards: ["gdpr", "iso27001", "nist"],
    currentTools: ["governance", "chat", "project"],
    currentChallenges: ["visibility", "inconsistency", "reporting", "updates"],
    processDuration: "continuous",
    complianceProcesses: "We maintain corporate policies through an integrated GRC platform and have formal review cycles quarterly with compliance specialists assigned to different regulatory domains. Our network infrastructure requires constant monitoring and certification.",
    softwareProcesses: "We use specialized systems for network compliance that are separate from our business compliance systems. We have continuous monitoring with automated alerts, but struggle with consistent reporting across different systems.",
    additionalInfo: "Our network infrastructure requires constant monitoring and certification. We struggle with the intersection of privacy compliance and security requirements."
  }
];

const ComplianceAI = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [demoData, setDemoData] = useState<ComplianceFormData | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(60); // 60 second countdown
  const [isDevelopment, setIsDevelopment] = useState(false);

  const pageRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Parallax scroll effects
  const { scrollYProgress } = useScroll({
    target: pageRef,
    offset: ["start start", "end start"]
  });

  const headerY = useTransform(scrollYProgress, [0, 0.2], [0, -30]);
  const headerOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0.8]);
  const featureScale = useTransform(scrollYProgress, [0.3, 0.4], [0.98, 1]);

  useEffect(() => {
    // Check if we're in development mode
    const hostname = window.location.hostname;
    const isDev = hostname === "localhost" || hostname === "127.0.0.1";
    setIsDevelopment(isDev);

    // Only populate with demo data in development mode
    if (isDev) {
      const randomIndex = Math.floor(Math.random() * mockCompanyProfiles.length);
      setDemoData(mockCompanyProfiles[randomIndex]);
    }
  }, []);

  // Function to load mock data with animated loading state
  const loadMockResponse = () => {
    setIsLoading(true);
    setError(null);
    setTimeRemaining(60); // Reset timer to 60 seconds

    console.log("Loading mock compliance AI response data");

    // Simulate API delay with minimum 5 seconds
    const simulatedDelay = Math.max(5000, Math.random() * 10000); // Between 5-10 seconds
    console.log(`Simulating API delay of ${simulatedDelay/1000} seconds`);

    setTimeout(() => {
      setResponse(mockComplianceAiResponse);
      setIsLoading(false);
    }, simulatedDelay);
  };

  const handleSubmit = async (formData: ComplianceFormData) => {
    setIsLoading(true);
    setError(null);
    setTimeRemaining(60); // Reset timer to 60 seconds

    // Only use mock data if explicitly requested via loadMockResponse()
    // We'll always try to use the actual API by default

    // Add retry logic for API requests
    const fetchWithRetry = async (url: string, options: RequestInit, maxRetries = 3): Promise<Response> => {
      let retries = 0;
      let lastError: Error | null = null;

      while (retries < maxRetries) {
        try {
          console.log(`API request attempt ${retries + 1}/${maxRetries}`);
          // Log the request for debugging
          console.log(`Sending request to ${url}`, { ...options });

          const response = await fetch(url, {
            ...options,
            // Only include essential CORS options
            mode: 'cors',
            // Keep headers simple
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            return response;
          }

          // For server errors (5xx), retry
          if (response.status >= 500) {
            console.warn(`Received ${response.status} from server, retrying...`);
            lastError = new Error(`Server error: ${response.status}`);
          } else {
            // For client errors (4xx), don't retry
            throw new Error(`Request failed with status ${response.status}: ${response.statusText}`);
          }
        } catch (err) {
          console.warn(`Fetch attempt ${retries + 1} failed:`, err);
          lastError = err instanceof Error ? err : new Error('Unknown error');

          // If it's a network error (like CORS) or a 5xx, we'll retry
          if (err instanceof TypeError && err.message.includes('fetch')) {
            console.warn('Network error, will retry');
          } else if (lastError.message.includes('Server error')) {
            console.warn('Server error, will retry');
          } else {
            // Other errors we won't retry
            throw lastError;
          }
        }

        // Wait before retrying, with exponential backoff
        const delay = Math.pow(2, retries) * 1000;
        console.log(`Waiting ${delay}ms before retry ${retries + 1}...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        retries++;
      }

      // If we get here, we've exhausted all retries
      throw lastError || new Error('Maximum retries reached');
    };

    try {
      console.log("Sending request to compliance AI API:", formData);

      // We'll always use the real API endpoint
      const apiUrl = `${API_CONFIG.API_BASE_URL}/compliance-ai/`;
      console.log("Full API URL:", apiUrl);

      // First, send a preflight OPTIONS request
      try {
        console.log("Sending OPTIONS preflight request");
        const preflightResponse = await fetch(apiUrl, {
          method: 'OPTIONS',
          headers: {
            'Origin': window.location.origin,
            'Access-Control-Request-Method': 'POST',
            'Access-Control-Request-Headers': 'Content-Type',
          },
        });
        console.log("Preflight response:", preflightResponse);
      } catch (err) {
        console.log("Preflight request failed, continuing with main request:", err);
      }

      // Then send the actual POST request
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin,
        },
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify(formData),
      });

      let data = await response.json();

      // Add debug logging
      console.log("API Response:", data);

      // Handle different response formats
      if (typeof data === 'string') {
        try {
          // Try to parse if it's a JSON string
          data = JSON.parse(data);
        } catch (e) {
          // If it's not valid JSON, wrap it in a content property
          data = { content: data };
        }
      }

      // Handle direct response object format (post-processed by backend)
      if (data.text !== undefined && data.advice !== undefined && data.diagram !== undefined) {
        console.log("Got well-formed response from API with text/advice/diagram properties");

        // Fix potential diagram issues if it's a string (for backward compatibility)
        if (typeof data.diagram === 'string') {
          fixDiagramSyntax(data);
        }
      }
      // Handle nested content property
      else if (data.content && typeof data.content === 'string') {
        try {
          const parsed = JSON.parse(data.content);
          if (parsed.text || parsed.advice || parsed.diagram) {
            data = parsed;
            if (typeof data.diagram === 'string') {
              fixDiagramSyntax(data);
            }
          }
        } catch (e) {
          console.error("Failed to parse content string:", e);
        }
      }
      // Handle Python dict-like representation
      else if (typeof data === 'object' && Object.keys(data).some(key => key.includes("'"))) {
        console.log("Got Python dict-like response, converting to proper JSON");
        try {
          // Convert Python dict format to proper JSON object
          const fixedData = {};

          Object.keys(data).forEach(key => {
            // Remove single quotes and fix key names
            const cleanKey = key.replace(/'/g, "");
            fixedData[cleanKey] = data[key];
          });

          // Set the properly formatted data
          data = fixedData;

          // Fix potential diagram issues if it's a string
          if (typeof data.diagram === 'string') {
            fixDiagramSyntax(data);
          }

          console.log("Converted response:", data);
        } catch (e) {
          console.error("Failed to convert Python dict format:", e);
        }
      }

      console.log("Final processed response for component:", data);

      // Helper function to fix diagram syntax issues
      function fixDiagramSyntax(dataObj) {
        if (!dataObj.diagram || typeof dataObj.diagram !== 'string') return;

        // Special case for the exact diagram format in the console log
        if (dataObj.diagram.includes('style F1 fill:#90EE90') && !dataObj.diagram.endsWith('end')) {
          console.log("Detected the exact diagram format from the console log");
          dataObj.diagram += "\n    end";
          console.log("Fixed the diagram by adding missing end tag");
          return;
        }

        // Count subgraph and end tags
        const subgraphCount = (dataObj.diagram.match(/subgraph/g) || []).length;
        const endCount = (dataObj.diagram.match(/end/g) || []).length;

        console.log(`Diagram analysis - Subgraph count: ${subgraphCount}, End count: ${endCount}`);

        // Add missing end tags
        if (subgraphCount > endCount) {
          for (let i = 0; i < (subgraphCount - endCount); i++) {
            dataObj.diagram += " end";
          }
        }

        // For diagrams with style declarations at the end
        if (dataObj.diagram.match(/style\s+[A-Z0-9]+\s+fill:#[0-9A-F]{6}$/i)) {
          console.log("Diagram ends with a style declaration, adding end tag");
          dataObj.diagram += "\n    end";
        }

        // Log the fixed diagram
        console.log("Fixed diagram:", dataObj.diagram);
      }

      // Ensure we show the loading animation for at least 5 seconds
      const elapsedTime = 60 - timeRemaining;
      if (elapsedTime < 5) {
        // Wait until we've shown the animation for at least 5 seconds
        setTimeout(() => {
          setResponse(data);
          setIsLoading(false);
        }, (5 - elapsedTime) * 1000);
      } else {
        setResponse(data);
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      if (err instanceof Error) {
        console.log('Error details:', {
          name: err.name,
          message: err.message,
          stack: err.stack,
          cause: err.cause
        });
      }
      
      // Show the actual error message to the user
      // We won't automatically fall back to mock data anymore
      setError(err instanceof Error ? 
        `API Error: ${err.message}. Use the "Load Mock Response" button to see demo data.` : 
        'An unknown error occurred. Use the "Load Mock Response" button to see demo data.');
      
      setIsLoading(false);
    }
  };

  // Platform features
  const complianceFeatures = [
    {
      icon: Shield,
      title: "Regulatory Analysis",
      description: "Our AI evaluates your compliance against major regulatory frameworks and identifies gaps."
    },
    {
      icon: FileCheck,
      title: "Process Optimization",
      description: "Transform manual workflows into efficient, automated processes that save time and reduce errors."
    },
    {
      icon: BarChart3,
      title: "Risk Reduction",
      description: "Identify compliance risks and receive prioritized recommendations to mitigate them effectively."
    }
  ];

  return (
    <div ref={pageRef} className="relative min-h-screen overflow-hidden">
      <Helmet>
        <title>Compliance AI Assistant | Verdea AI</title>
        <meta
          name="description"
          content="Optimize your compliance processes with AI. Get personalized recommendations for your team's workflow."
        />
      </Helmet>

      {/* Background Elements - Apple style with purple theme */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        {/* Primary gradient blur */}
        <div
          className="absolute top-0 right-0 w-[50vw] h-[50vh] blur-3xl opacity-30"
          style={{
            background: 'radial-gradient(circle at center, rgba(124, 58, 237, 0.3) 0%, rgba(124, 58, 237, 0) 70%)', // Purple-600
            transform: 'translate(20%, -20%)'
          }}
        />

        {/* Secondary gradient blur */}
        <div
          className="absolute bottom-0 left-0 w-[40vw] h-[40vh] blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle at center, rgba(165, 180, 252, 0.3) 0%, rgba(165, 180, 252, 0) 70%)', // Indigo-300
            transform: 'translate(-20%, 20%)'
          }}
        />

        {/* Subtle dot grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: 'radial-gradient(rgba(124, 58, 237, 0.2) 1px, transparent 1px)', // Purple-600
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      {/* Navbar */}
      <header className="sticky top-0 z-50">
        <NavBar />
      </header>

      {/* Hero Section - Apple Style */}
      <section className="pt-36 flex items-center justify-center overflow-hidden">
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            ref={contentRef}
            style={{ y: headerY, opacity: headerOpacity }}
            className="max-w-5xl mx-auto"
          >
            {/* Platform header */}
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="text-4xl md:text-6xl font-medium tracking-tight text-gray-900 dark:text-gray-100 mb-6 leading-tight">
                  AI Compliance
                  <br />
                  <span className="bg-gradient-to-r from-purple-600 to-indigo-500 bg-clip-text text-transparent">
                    Process Optimizer
                  </span>
                </h1>

                <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 mb-8 md:mb-10 max-w-3xl mx-auto leading-relaxed">
                  Discover how AI can transform your compliance workflows. Share details about your
                  current processes to receive personalized optimization recommendations.
                </p>
              </motion.div>

              {/* Dev Mode Button */}
              {isDevelopment && !response && !isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="mb-8"
                >
                  <Button
                    onClick={loadMockResponse}
                    variant="outline"
                    className="flex items-center gap-2 mx-auto"
                  >
                    <Wrench className="w-4 h-4" />
                    <span>Load Mock Response (Dev Only)</span>
                  </Button>
                </motion.div>
              )}

              {/* Feature cards */}
              {!response && !isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="grid md:grid-cols-3 gap-6 md:gap-8 "
                >
                  {complianceFeatures.map((feature, index) => {
                    const Icon = feature.icon;
                    return (
                      <div
                        key={index}
                        className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 transition-all duration-300 hover:shadow-md"
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className="h-12 w-12 rounded-full bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center text-purple-600 dark:text-purple-400 mb-4">
                            <Icon className="h-6 w-6" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{feature.title}</h3>
                          <p className="text-gray-500 dark:text-gray-400 text-sm">{feature.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content Section */}
      <motion.section
        style={{ scale: featureScale }}
        className="py-16 md:pt-8 md:pb-20"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            {/* Show the loading animation when form is submitted and data is loading */}
            {isLoading ? (
              <LoadingAnimation
                timeRemaining={timeRemaining}
                setTimeRemaining={setTimeRemaining}
              />
            ) : !response ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <ComplianceAIForm onSubmit={handleSubmit} isLoading={isLoading} error={error} initialData={demoData} />
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <ComplianceAIResponse
                    response={response}
                    onReset={() => setResponse(null)} isLoading={false} error={undefined}                />
              </motion.div>
            )}
          </div>
        </div>
      </motion.section>

      {/* Footer */}
      <footer>
        <Footer />
      </footer>
    </div>
  );
};

export default ComplianceAI;
